/**
 * Хранилище мебели пользователя и открытого проекта на бэкенде (`/furniture`).
 * Остальной код читает модули, шкафы и кухни через `useCustomFurnitureStore`
 * и каталог («Моя мебель»).
 */
import { create } from "zustand";
import {
	Api,
	type FurnitureData,
	type FurnitureItemDto,
	type KitchenRunSectionsData,
	type WardrobeCellData,
} from "@/api";
import { authUserId, useAuthStore } from "@/shared/store/auth/authStore";
import type { KitchenDesign, ModuleDesign, WardrobeDesign } from "./types";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const normalizeProjectId = (id: string | null | undefined): string | null =>
	id && UUID_REGEX.test(id) ? id : null;

interface CustomFurnitureState {
	designs: WardrobeDesign[];
	kitchens: KitchenDesign[];
	modules: ModuleDesign[];
	activeProjectId: string | null;
	setActiveProjectId: (projectId: string | null) => void;
	/** Загрузить мебель пользователя (и текущего/указанного проекта) с бэкенда */
	loadCustomFurniture: (projectId?: string | null) => Promise<void>;
	/** Очистить мебель в памяти (при выходе из аккаунта) */
	clearCustomFurniture: () => void;
	/** Сохранить шкаф: новый добавить, существующий (по id) заменить; время сохранения ставит стор */
	saveDesign: (design: Omit<WardrobeDesign, "updatedAt">) => void;
	removeDesign: (id: string) => void;
	saveKitchen: (kitchen: Omit<KitchenDesign, "updatedAt">) => void;
	removeKitchen: (id: string) => void;
	saveModule: (module: Omit<ModuleDesign, "updatedAt">) => void;
	removeModule: (id: string) => void;
}

/** Заменить по id или добавить в конец */
const upsert = <T extends { id: string }>(list: T[], item: T) =>
	list.some((x) => x.id === item.id)
		? list.map((x) => (x.id === item.id ? item : x))
		: [...list, item];

const parseFurnitureItems = (items: FurnitureItemDto[]) => {
	const designs: WardrobeDesign[] = [];
	const kitchens: KitchenDesign[] = [];
	const modules: ModuleDesign[] = [];

	for (const dto of items) {
		const { data } = dto;
		if (data.wardrobe) {
			designs.push({
				...data.wardrobe,
				id: dto.id,
				name: data.name,
				projectId: dto.projectId,
				updatedAt: dto.updatedAt,
			});
		} else if (data.kitchen) {
			kitchens.push({
				...data.kitchen,
				id: dto.id,
				name: data.name,
				projectId: dto.projectId,
				updatedAt: dto.updatedAt,
			});
		} else if (data.module) {
			modules.push({
				...data.module,
				id: dto.id,
				name: data.name,
				projectId: dto.projectId,
				updatedAt: dto.updatedAt,
			});
		}
	}

	return { designs, kitchens, modules };
};

const buildWardrobeData = (item: WardrobeDesign): FurnitureData => {
	const cells: Record<string, WardrobeCellData> = {};
	for (const [key, cell] of Object.entries(item.cells)) {
		if (
			cell.fill === null &&
			cell.shelves === null &&
			cell.door === null &&
			cell.direction === null
		)
			continue;
		cells[key] = cell;
	}
	const { id: _id, name, projectId: _projectId, updatedAt: _updatedAt, ...wardrobe } = item;
	return {
		name,
		kitchen: null,
		module: null,
		wardrobe: {
			...wardrobe,
			cells,
		},
	};
};

const buildKitchenData = (item: KitchenDesign): FurnitureData => {
	let sections: Record<string, KitchenRunSectionsData> | null = null;
	if (item.sections && Object.keys(item.sections).length > 0) {
		sections = {};
		for (const [runKey, runData] of Object.entries(item.sections)) {
			if (!runData) continue;
			sections[runKey] = runData;
		}
	}
	const { id: _id, name, projectId: _projectId, updatedAt: _updatedAt, ...kitchen } = item;
	return {
		name,
		wardrobe: null,
		module: null,
		kitchen: {
			...kitchen,
			sections,
		},
	};
};

const buildModuleData = (item: ModuleDesign): FurnitureData => {
	const { id: _id, name, projectId: _projectId, updatedAt: _updatedAt, ...module } = item;
	return {
		name,
		wardrobe: null,
		kitchen: null,
		module,
	};
};

const syncSaveToBackend = async (
	id: string,
	projectId: string | null | undefined,
	data: FurnitureData,
	existed: boolean,
) => {
	const resolvedProjectId =
		projectId !== undefined
			? normalizeProjectId(projectId)
			: normalizeProjectId(useCustomFurnitureStore.getState().activeProjectId);
	const payload = {
		id,
		projectId: resolvedProjectId,
		data,
	};
	if (existed) {
		const res = await Api.Furniture.putApiFurnitureById({ id }, payload);
		if (res.error !== null) {
			await Api.Furniture.postApiFurniture(payload);
		}
		return;
	}
	await Api.Furniture.postApiFurniture(payload);
};

export const useCustomFurnitureStore = create<CustomFurnitureState>()((set, get) => ({
	designs: [],
	kitchens: [],
	modules: [],
	activeProjectId: null,
	setActiveProjectId: (projectId) => set({ activeProjectId: normalizeProjectId(projectId) }),
	loadCustomFurniture: async (projectId) => {
		const resolvedProjectId =
			projectId !== undefined ? normalizeProjectId(projectId) : get().activeProjectId;
		if (projectId !== undefined) {
			set({ activeProjectId: resolvedProjectId });
		}
		const userId = authUserId(useAuthStore.getState().accessToken);
		const response = await Api.Furniture.getApiFurniture(
			resolvedProjectId ? { projectId: resolvedProjectId } : ({} as { projectId: string }),
		);
		if (
			authUserId(useAuthStore.getState().accessToken) !== userId ||
			get().activeProjectId !== resolvedProjectId
		)
			return;
		if (response.error !== null || response.data === null) return;
		set(parseFurnitureItems(response.data));
	},
	clearCustomFurniture: () =>
		set({ designs: [], kitchens: [], modules: [], activeProjectId: null }),
	saveDesign: (draft) => {
		const existed = get().designs.some((d) => d.id === draft.id);
		const item: WardrobeDesign = { ...draft, updatedAt: new Date().toISOString() };
		set((s) => ({ designs: upsert(s.designs, item) }));
		void syncSaveToBackend(item.id, item.projectId, buildWardrobeData(item), existed);
	},
	removeDesign: (id) => {
		set((s) => ({ designs: s.designs.filter((d) => d.id !== id) }));
		void Api.Furniture.deleteApiFurnitureById({ id });
	},
	saveKitchen: (draft) => {
		const existed = get().kitchens.some((k) => k.id === draft.id);
		const item: KitchenDesign = { ...draft, updatedAt: new Date().toISOString() };
		set((s) => ({ kitchens: upsert(s.kitchens, item) }));
		void syncSaveToBackend(item.id, item.projectId, buildKitchenData(item), existed);
	},
	removeKitchen: (id) => {
		set((s) => ({ kitchens: s.kitchens.filter((k) => k.id !== id) }));
		void Api.Furniture.deleteApiFurnitureById({ id });
	},
	saveModule: (draft) => {
		const existed = get().modules.some((m) => m.id === draft.id);
		const item: ModuleDesign = { ...draft, updatedAt: new Date().toISOString() };
		set((s) => ({ modules: upsert(s.modules, item) }));
		void syncSaveToBackend(item.id, item.projectId, buildModuleData(item), existed);
	},
	removeModule: (id) => {
		set((s) => ({ modules: s.modules.filter((m) => m.id !== id) }));
		void Api.Furniture.deleteApiFurnitureById({ id });
	},
}));

export const findWardrobeDesign = (id: string): WardrobeDesign | null =>
	useCustomFurnitureStore.getState().designs.find((design) => design.id === id) ?? null;

export const findKitchenDesign = (id: string): KitchenDesign | null =>
	useCustomFurnitureStore.getState().kitchens.find((kitchen) => kitchen.id === id) ?? null;

export const findModuleDesign = (id: string): ModuleDesign | null =>
	useCustomFurnitureStore.getState().modules.find((module) => module.id === id) ?? null;
